import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, OnDestroy, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

export type XpToastSeverity = 'success' | 'info' | 'warn' | 'error';

export interface XpToast {
	id: number;
	severity: XpToastSeverity;
	summary: string;
	detail: string;
	life?: number;
	sticky?: boolean;
}

const SEVERITY_ICONS: Record<XpToastSeverity, string> = {
	success: 'check',
	info: 'info',
	warn: 'warning',
	error: 'highlight_off'
};

@Component({
	selector: 'xp-toasts',
	standalone: true,
	imports: [CommonModule, MatIconModule],
	templateUrl: './xp-toasts.component.html',
	styleUrls: ['./xp-toasts.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [
		trigger('toast', [
			transition(':enter', [
				style({ transform: 'translateY(100%)', opacity: 0 }),
				animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
			]),
			transition(':leave', [
				animate('250ms ease-in', style({ height: 0, opacity: 0, transform: 'translateY(-100%)' }))
			])
		])
	]
})
export class XpToastsComponent implements OnChanges, OnDestroy {
	toasts = input.required<readonly XpToast[]>();
	life = input(3000);
	closeLabel = input('Close');
	dismiss = output<XpToast>();

	private readonly timers = new Map<number, ReturnType<typeof setTimeout> | undefined>();
	private readonly paused = new Set<number>();

	ngOnChanges(): void {
		const shown = new Set(this.toasts().map(toast => toast.id));
		this.timers.forEach((timer, id) => {
			if (!shown.has(id)) {
				clearTimeout(timer);
				this.timers.delete(id);
				this.paused.delete(id);
			}
		});
		this.toasts()
			.filter(toast => !this.timers.has(toast.id))
			.forEach(toast => this.startTimer(toast));
	}

	ngOnDestroy(): void {
		this.timers.forEach(timer => clearTimeout(timer));
		this.timers.clear();
	}

	icon(toast: XpToast): string {
		return SEVERITY_ICONS[toast.severity];
	}

	trackById(_index: number, toast: XpToast): number {
		return toast.id;
	}

	onPointerEnter(toast: XpToast): void {
		if (this.timers.get(toast.id) !== undefined) {
			this.stopTimer(toast);
			this.paused.add(toast.id);
		}
	}

	onPointerLeave(toast: XpToast): void {
		if (this.paused.delete(toast.id)) {
			this.startTimer(toast);
		}
	}

	onClose(toast: XpToast): void {
		this.stopTimer(toast);
		this.paused.delete(toast.id);
		this.dismiss.emit(toast);
	}

	private startTimer(toast: XpToast): void {
		clearTimeout(this.timers.get(toast.id));
		this.timers.set(toast.id, toast.sticky ? undefined : setTimeout(() => {
			this.timers.set(toast.id, undefined);
			this.dismiss.emit(toast);
		}, toast.life ?? this.life()));
	}

	private stopTimer(toast: XpToast): void {
		if (this.timers.has(toast.id)) {
			clearTimeout(this.timers.get(toast.id));
			this.timers.set(toast.id, undefined);
		}
	}
}
